/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1669593600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon Nov 28 2022 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "auth0",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Registering your M2M for Hightouch",
          "depth": 1,
          "id": "registering-your-m2m-for-hightouch"
        }, {
          "value": "Syncing rows",
          "depth": 0,
          "id": "syncing-rows"
        }, {
          "value": "Sync modes",
          "depth": 1,
          "id": "sync-modes"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      em: "em",
      strong: "strong",
      img: "img",
      h3: "h3",
      a: "a",
      ul: "ul",
      li: "li",
      code: "code"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsxs(_components.p, {
        children: ["After selecting Auth0 from our Destinations catalog, you will be prompted to enter\nyour ", _jsx(_components.em, {
          children: "Auth0 domain"
        }), ", ", _jsx(_components.em, {
          children: "Client ID"
        }), ", and ", _jsx(_components.em, {
          children: "Client secret"
        }), " from your ", _jsx(_components.strong, {
          children: "registered\nmachine-to-machine (M2M) application for Hightouch"
        }), ". You should find this by navigating\nto your Auth0 dashboard -> Applications dropdown -> Applications -> select corresponding\nHightouch M2M. This particular M2M will give Hightouch permission to connect with your\nManagement API and update your users.\n", _jsx(_components.img, {
          src: "destinations/destination-auth0-config.png",
          alt: ""
        })]
      }), "\n", _jsx(_components.h3, {
        id: "registering-your-m2m-for-hightouch",
        children: "Registering your M2M for Hightouch"
      }), "\n", _jsxs(_components.p, {
        children: ["If you do not have a registered M2M for Hightouch, refer to\n", _jsx(_components.a, {
          href: "https://auth0.com/docs/get-started/create-apps/machine-to-machine-apps",
          children: "this Auth0 doc"
        }), " on\nhow to get started. You will then need to select the following configuration:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Select the ", _jsx(_components.strong, {
            children: "Auth0 Management API"
          }), "\n", _jsx(_components.img, {
            src: "destinations/destination-auth0-api-selection.png",
            alt: ""
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Check the ", _jsx(_components.strong, {
            children: "update:users"
          }), " permission.\n", _jsx(_components.img, {
            src: "destinations/destination-auth0-permissions.png",
            alt: ""
          })]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["After you authorize, you should go to the M2M's settings to obtain the required keys.\n", _jsx(_components.img, {
          src: "destinations/destination-auth0-m2m-settings.png",
          alt: ""
        })]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Keep in mind you might have to go back and add to the permissions for\npotential future features."
        })
      }), "\n", _jsx(_components.h2, {
        id: "syncing-rows",
        children: "Syncing rows"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports syncing to the following Auth0 objects:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "Users"
          })
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "User objects in Auth0 will use the external ID as its own ID. Therefore, the user's source ID\n(that is, external ID) must be present to properly match the user."
      }), "\n", _jsx(_components.h3, {
        id: "sync-modes",
        children: "Sync modes"
      }), "\n", _jsxs(_components.p, {
        children: ["This integration only supports ", _jsx(_components.strong, {
          children: "Update"
        }), " mode.\nIn this mode, the destination will update the mapped attributes and metadata to the user objects.\nIt will not add new users."]
      }), "\n", _jsx(_components.h3, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "Records can be matched from your source to Auth0 workspace by the external user's ID only."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-auth0-external-mapping.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["You can sync columns from your source to Auth0's user attributes, including the app and user's metadata.\nYou can view this ", _jsx(_components.a, {
          href: "https://auth0.com/docs/users/user-profiles/user-profile-structure",
          children: "list for the supported Auth0 user attributes"
        }), ".\nThe app and user metadata holds custom data so can take any shape or form."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.img, {
          src: "destinations/destination-auth0-field-mappings.png",
          alt: ""
        }), "\n", _jsx(_components.img, {
          src: "destinations/destination-auth0-metadata-mappings.png",
          alt: ""
        })]
      })]
    });
  }
}
export default MDXContent;
